import React, { useState, KeyboardEvent, useEffect, useCallback } from 'react';
import DeleteIcon from './delete-icon.ui';

import styles from './tag-input.module.css';

type TagInputUiProps = {
  id?: string;
  values: string[];
  placeholder?: string;
  onAdd: (skill: string) => void;
  onRemove: (skill: string) => void;
  onSaveRef?: (saveFn: () => void) => void;
  error?: string;
  labelText?: string;
  disabled?: boolean;
  max?: number;
}

const TagInputUi = (props: TagInputUiProps) => {
  const {
    id,
    values,
    placeholder = '',
    onAdd,
    onRemove,
    onSaveRef,
    error,
    labelText,
    disabled,
    max = 50,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addValue();
    }
  };

  const addValue = useCallback(() => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !values.includes(trimmedValue)) {
      // Only allow letters, numbers, +, #, and common tech symbols
      const validSkill = /^[a-zA-Z0-9+#+&.\-_\s]+$/.test(trimmedValue);
      if (validSkill) {
        onAdd(trimmedValue);
        setInputValue('');
        setErrorMessage('');
      } else {
        setErrorMessage('Invalid characters. Please use letters, numbers, and common symbols only.');
      }
    }
  }, [inputValue, values, onAdd]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setErrorMessage('');
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addValue();
    }
  };

  useEffect(() => {
    if (onSaveRef) {
      onSaveRef(addValue);
    }
  }, [onSaveRef, addValue]);

  return (
    <div>
      <label htmlFor={id} className={styles.label}>
        {labelText}
      </label>
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={max}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
      />

      {values.length > 0 && (
        <div className={styles.item}>
          {values.map((val, index) => (
            <div key={index} className={styles.chip}>
              <div className={styles.text}>{val.length > 10 ? val.substring(0, 10) + '...' : val}</div>
              <DeleteIcon onClick={() => onRemove(val)} color={'var(--white-color'} />
            </div>
          ))}
        </div>
      )}

      {errorMessage && <p className={styles.error}>{errorMessage}</p>}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};

TagInputUi.defaultProps = {
  id: '',
  labelText: '',
  error: '',
  disabled: false,
};

export default TagInputUi;
